import { supabase } from '../../integrations/supabase/client';
import type { IConfigurationRepository, ClientConfiguration, ConfigurationTemplate } from '../interfaces/IConfigurationRepository';
import { webhooks } from '../../utils/webhookTrigger';
import { AuditLogger } from '../../utils/auditLogger';

export class SupabaseConfigurationRepository implements IConfigurationRepository {
  async getConfig(clientId: string, configKey: string, configType: string): Promise<any> {
    const { data, error } = await (supabase.rpc as any)('get_client_config', {
      p_client_id: clientId,
      p_config_key: configKey,
      p_config_type: configType
    });

    if (error) throw error;
    return data;
  }

  async setConfig(data: {
    client_id: string;
    config_key: string;
    config_value: any;
    config_type: string;
  }): Promise<ClientConfiguration> {
    const { data: config, error } = await ((supabase as any)
      .from('client_configurations')
      .upsert({
        client_id: data.client_id,
        config_key: data.config_key,
        config_value: data.config_value,
        config_type: data.config_type
      }, { onConflict: 'client_id,config_key,config_type' })
      .select()
      .maybeSingle());

    if (error) throw error;
    if (!config) throw new Error('Failed to create configuration');

    // Trigger webhook event
    webhooks.configUpdated(config.id, data.client_id, {
      config_key: data.config_key,
      config_type: data.config_type,
      is_inherited: config.is_inherited
    });

    // Audit log
    AuditLogger.logConfigAction('created', config.id, data.client_id, data.config_key, {
      config_type: data.config_type,
    }, undefined, { config_value: data.config_value });

    return config as any;
  }

  async getAllConfigs(clientId: string): Promise<ClientConfiguration[]> {
    const { data, error } = await (supabase as any)
      .from('client_configurations')
      .select('*')
      .eq('client_id', clientId)
      .order('config_type', { ascending: true });

    if (error) throw error;
    return (data || []) as any;
  }

  async deleteConfig(id: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('client_configurations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getTemplates(): Promise<ConfigurationTemplate[]> {
    const { data, error } = await supabase
      .from('configuration_templates')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data || [];
  }

  async applyTemplate(tenantId: string, templateId: string): Promise<void> {
    const { data: template, error: templateError } = await supabase
      .from('configuration_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (templateError) throw templateError;

    const configsToInsert = Object.entries(template.default_values as Record<string, any>).map(([key, value]) => ({
      client_id: tenantId,
      config_key: key,
      config_value: value as any,
      config_type: template.template_type
    }));

    const { error } = await (supabase as any)
      .from('client_configurations')
      .upsert(configsToInsert, { onConflict: 'client_id,config_key,config_type' });

    if (error) throw error;
  }
}
